// Imports
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import styled from 'styled-components/macro';
import Button from '../../../../../../shareables/foundation/front-end/components/buttons/standard';
import { useAppDispatch, useAppSelector } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import PlaceUpserter, { Address } from '../../../../../../shareables/front-end/place-upserter';
import APIForm, { APIFormOptions } from '../../../../../../shareables/integrations/api/components/form';
import apiActions from '../../../../../../shareables/integrations/api/redux/actions';
import {
	PostAccountParameters,
	PostAccountResponse,
} from '../../../../../../shareables/types/api/main/website/account/post';
import { GetRegistrationResponse } from '../../../../../../shareables/types/api/main/website/settings/registration/get';
import UpsertMember from '../shared-components/upsert-member/upsert-member';
import RegistrationFields from '../shared-components/registration-fields';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import useRecoveredState from '../../../../../../shareables/foundation/front-end/utils/use-recovered-state';
import { GetMemberResponse } from '../../../../../../shareables/types/api/main/website/member/get';
import selfActions from '../../../../../../redux/self/actions';
import PageTitle from '../../shared-components/page-title';
import { fieldContainerBottomMargin } from '../../../../../../shareables/foundation/front-end/components/forms/field-container';


// Styled components
const RegisteredNote = styled.div`
	line-height: 1.3125rem;
	
	p:first-child {
		margin-bottom: 1.3125rem;
	}
`;

const Panel = styled.div`
	margin-bottom: 1.25rem;
	border: 0.0625rem solid #ddd;
	border-radius: 0.25rem;
	box-shadow: 0 0.0625rem 0.0625rem rgba(0, 0, 0, 0.05);
`;

const PanelHeading = styled.div`
	padding: 0.625rem 0.9375rem;
	border-bottom: 0.0625rem solid #ddd;
	border-top-left-radius: 0.1875rem;
	border-top-right-radius: 0.1875rem;
	color: #333;
	background-color: #f5f5f5;
	line-height: 1.1rem;
	
	h3 {
		font-weight: 500;
		font-size: 1rem;
	}
`;

const PanelRegisterFormBody = styled.div`
	padding: 0.9375rem;
	margin-top: -1.5625rem;
	
	div:first-of-type {
		margin-bottom: 0;
	}
	
	label {
		margin-top: 1.5625rem;
	}
`;

const PanelAdditionalFormsBody = styled.div`
	padding: 0.9375rem;
`;

const Card = styled.div`
	background: #eee;
	border-radius: 0.1875rem;
	border: 0.0625rem solid #ddd;
	padding: 0.9375rem;
	margin-bottom: 1.5rem;
`;

const AddButtonContainer = styled.div`
	text-align: center;
`;

const RemoveButtonContainer = styled.div`
	margin-top: ${fieldContainerBottomMargin};
	text-align: center;
`;

const DemoNote = styled.div`
	margin-bottom: 1.5625rem;
`;


// Define the accepted props
interface Props {
	storybookRegistrationSettings?: GetRegistrationResponse;
	storybookIncludeMemberList?: boolean[];
	storybookIncludeAddressList?: boolean[];
	storybookAccountTypeID?: number;
	storybookFormSubmitted?: true;
}


// Function component
const Register: React.FC<Props> = ({
	storybookRegistrationSettings,
	storybookIncludeMemberList,
	storybookIncludeAddressList,
	storybookAccountTypeID,
	storybookFormSubmitted,
}) => {
	// Use React Router functionality
	const [searchParams] = useSearchParams();
	
	
	// Use state
	const [registrationSettings, setRegistrationSettings] = useState<GetRegistrationResponse | null>(
		storybookRegistrationSettings ?? null
	);
	
	const [accountTypeID, setAccountTypeID] = useState<number | undefined>(storybookAccountTypeID);
	const [formSubmitted, setFormSubmitted] = useState(storybookFormSubmitted ?? searchParams.get('registered') !== null);
	const [includeMemberList, setIncludeMemberList] = useRecoveredState<boolean[]>(
		storybookIncludeMemberList ?? [false],
		'include-member-list'
	);
	
	const [includeAddressList, setIncludeAddressList] = useRecoveredState<boolean[]>(
		storybookIncludeAddressList ?? [false],
		'include-address-list'
	);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	const signedIn = useAppSelector((state) => state.common.user.signedIn);
	const member = useAppSelector((state) => state.self.member);
	const user = useAppSelector((state) => state.common.user);
	const phone = useAppSelector((state) => state.self.phone);
	
	
	// Load registration settings on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetRegistrationResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/settings/registration`,
				completion: (json) => {
					setRegistrationSettings(json.response);
				},
			})
		);
	}, [dispatch]);
	
	
	// Construct present fields
	const presentFields = useMemo<(keyof Address)[]>(() => {
		const fields: (keyof Address)[] = [
			'countryCode',
			'division1',
			'division2',
			'mailingAddress',
			'postalCode',
			'regionID',
			'streetAddress',
			'type',
			'unitNumber',
			'unitType',
		];
		
		if (registrationSettings?.extraAddressDetail) {
			fields.push('extraAddressDetail');
		}
		
		if (
			registrationSettings?.accountTypes.find((accountType) => accountType.id === accountTypeID)?.requireBillingAddress
		) {
			fields.push('billingAddress');
		}
		
		if ((registrationSettings?.locations.length ?? 0) > 1) {
			fields.push('alternateLocationID');
		}
		
		return fields;
	}, [registrationSettings, accountTypeID]);
	
	
	// Handle signed in users who are members of the current website
	if (!formSubmitted && signedIn && member) {
		return <Navigate to='/' />;
	}
	
	
	// If loading, then display nothing
	if (registrationSettings === null) {
		return null;
	}
	
	
	// Build form options
	const formOptions: APIFormOptions<PostAccountParameters, undefined, PostAccountResponse> = {
		action: 'POST',
		uri: `/website/${window.location.hostname}/account`,
		submitButton: {
			text: 'Register',
		},
		appearsOnBackground: false,
		recaptcha: {
			enabled: true,
		},
	};
	
	
	// Handle removing a member
	const handleRemoveMember = (index: number) => {
		const currentMembers = [...includeMemberList];
		
		currentMembers[index] = false;
		
		setIncludeMemberList(currentMembers);
	};
	
	
	// Handle removing an address
	const handleRemoveAddress = (index: number) => {
		const currentAddresses = [...includeAddressList];
		
		currentAddresses[index] = false;
		
		setIncludeAddressList(currentAddresses);
	};
	
	
	// Handle adding a member
	const handleAddMember = () => {
		const currentMembers = [...includeMemberList, false];
		
		currentMembers[includeMemberList.length - 1] = true;
		
		setIncludeMemberList(currentMembers);
	};
	
	
	// Handle adding an address
	const handleAddAddress = () => {
		const currentAddresses = [...includeAddressList, false];
		
		currentAddresses[includeAddressList.length - 1] = true;
		
		setIncludeAddressList(currentAddresses);
	};
	
	
	// Handle form completion
	const handleFormCompletion = (json: SuccessfulAPIResponse<PostAccountResponse>) => {
		// Sign in
		if (!signedIn) {
			window.location.href = `/oauth/set-session?accessToken=${json.response.accessToken}&refreshToken=${json.response.refreshToken}&redirect-uri=${encodeURIComponent('/account/register?registered=true')}`;
			
			return;
		}
		
		
		// Update the form submitted state
		setFormSubmitted(true);
		
		
		// Update the member state
		void dispatch(
			apiActions.call<undefined, undefined, GetMemberResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/member`,
				completion: (json) => {
					dispatch(selfActions.storeMember(json.response));
				},
			})
		);
	};
	
	
	// Build fields
	const fields = (
		<React.Fragment>
			<Panel>
				<PanelHeading>
					<h3>Your information</h3>
				</PanelHeading>
				<PanelRegisterFormBody>
					<RegistrationFields
						accountTypes={registrationSettings.accountTypes}
						handleAccountTypeChange={setAccountTypeID}
						password={!signedIn}
						askGender={registrationSettings.askGender}
						disabled={signedIn}
						generalSettings={
							signedIn
								? {
										firstName: user.firstName ?? '',
										lastName: user.lastName ?? '',
										email: user.email ?? '',
										cellPhone: phone,
										gender: null,
									}
								: undefined
						}
					/>
				</PanelRegisterFormBody>
			</Panel>
			
			<Panel>
				<PanelHeading>
					<h3>Additional household members</h3>
				</PanelHeading>
				<PanelAdditionalFormsBody>
					{includeMemberList.map((includeMember, memberIndex) => (
						<AnimateHeight height={includeMember ? 'auto' : 0} duration={400} key={memberIndex} animateOpacity>
							<Card>
								<UpsertMember
									askGender={registrationSettings.askGender}
									parentParameter={`members[${memberIndex}]`}
									storybookRegistrationSettings={storybookRegistrationSettings}
									isFormOpen={includeMember}
								/>
								
								<RemoveButtonContainer>
									<Button type='button' color='danger' smaller onClick={() => handleRemoveMember(memberIndex)}>
										<FontAwesomeIcon icon={faTrash} />
										<span>Remove this member</span>
									</Button>
								</RemoveButtonContainer>
							</Card>
						</AnimateHeight>
					))}
					<AddButtonContainer>
						<Button type='button' color='plain' border smaller onClick={handleAddMember}>
							<FontAwesomeIcon icon={faPlus} />
							<span>Add member</span>
						</Button>
					</AddButtonContainer>
				</PanelAdditionalFormsBody>
			</Panel>
			
			<AnimateHeight
				height={
					accountTypeID &&
					registrationSettings.accountTypes.find((accountType) => accountType.id === accountTypeID)
						?.addressRequirement !== 'NONE'
						? 'auto'
						: 0
				}
				duration={400}
				animateOpacity
			>
				<Panel>
					<PanelHeading>
						<h3>Address(es)</h3>
					</PanelHeading>
					<PanelAdditionalFormsBody>
						{includeAddressList.map((includeAddress, addressIndex) => (
							<AnimateHeight height={includeAddress ? 'auto' : 0} duration={400} key={addressIndex} animateOpacity>
								<Card>
									<PlaceUpserter
										presentFields={presentFields}
										excludedFields={!includeAddress ? presentFields : undefined}
										fallbackCountry={registrationSettings.twoLetterCode}
										parentParameter={`addresses[${addressIndex}]`}
										locations={registrationSettings.locations}
										extraAddressDetail={
											includeAddress ? (registrationSettings.extraAddressDetail ?? undefined) : undefined
										}
										optionalUnitNumber={!includeAddress || registrationSettings.unitNumberRequirement === 'OPTIONAL'}
										hiddenUnitNumber={registrationSettings.unitNumberRequirement === 'HIDDEN'}
									/>
									
									<RemoveButtonContainer>
										<Button type='button' color='danger' smaller onClick={() => handleRemoveAddress(addressIndex)}>
											<FontAwesomeIcon icon={faTrash} />
											<span>Remove this address</span>
										</Button>
									</RemoveButtonContainer>
								</Card>
							</AnimateHeight>
						))}
						<AddButtonContainer>
							<Button type='button' color='plain' border smaller onClick={handleAddAddress}>
								<FontAwesomeIcon icon={faPlus} />
								<span>Add address</span>
							</Button>
						</AddButtonContainer>
					</PanelAdditionalFormsBody>
				</Panel>
			</AnimateHeight>
		</React.Fragment>
	);
	
	
	// Return JSX
	return (
		<React.Fragment>
			<PageTitle>Register</PageTitle>
			
			{formSubmitted ? (
				registrationSettings.requireAccountApproval ? (
					<RegisteredNote>
						<p>
							Thanks for registering! Your account will need to be reviewed by the website administrator(s) before it
							can be used to access protected content. You’ll receive an email as soon as your account is reviewed.
						</p>
						<p>
							In the meantime, we recommend adding additional information to your profile to help other members get to
							know you. To do so, <Link to='../member-settings/profile'>follow this link</Link>.
						</p>
					</RegisteredNote>
				) : (
					<RegisteredNote>
						Thanks for registering! Your account is now active and ready for use. We recommend adding additional
						information to your profile to help other members get to know you. To do so,{' '}
						<Link to='../member-settings/profile'>follow this link</Link>.
					</RegisteredNote>
				)
			) : window.location.hostname === 'demo.communitysite.com' ? (
				<React.Fragment>
					{fields}
					<DemoNote>
						<em>Registration has been disabled on the demo website</em>
					</DemoNote>
				</React.Fragment>
			) : (
				<APIForm options={formOptions} onCompletion={handleFormCompletion}>
					{fields}
				</APIForm>
			)}
		</React.Fragment>
	);
};

export default Register;
